import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { InterPoint, Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Icon,
  Illustration,
  ListRow,
  Typography,
} from '@saviynt/design-system';

import {
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_NEU_MED,
  CF_NEU_STL,
  CF_SEC_BLD,
  LIMIT_INLINE_COUNT,
} from '../../../constants';
import { useEffectAfterMount, useInViewObserver } from '../../../hooks';
import { getLogoEndpointUrl } from '../../../utils';
import { WRAPPER_MODE } from '../../UiWrapper';
import { MAX_COUNT } from '../constants';
import { ListControls, ListSkeleton } from '../Shared';
import {
  type CustomListModalTypes,
  type EndpointParams,
  type ServiceAccountListResponse,
} from '../types';

import './ServiceAccount.css';

interface TrackedListItem extends ServiceAccountListResponse {
  tracker: {
    id: number;
    isChecked: boolean;
  };
}

interface ServiceAccountListItemProps {
  isLoading: boolean;
  listItem: TrackedListItem;
  handleClick: (id: number) => void;
}

function ServiceAccountListItem({
  isLoading,
  listItem,
  handleClick,
}: ServiceAccountListItemProps) {
  const {
    name,
    endpointLogo,
    endpointname,
    description,
    tracker: { id, isChecked },
  } = listItem;

  const isDisabled = isLoading;

  return (
    <ListRow
      isCheckboxDisabled={isDisabled}
      leadingComp={<ApplicationIcon logo='Account' size='medium' />}
      centerContent={
        <Box tag='div' className='ServiceAccountListItem-contentWrapper'>
          <Typography kind='h3' color={CF_NEU_BLD}>
            {name ?? (
              <FormattedMessage
                id='YoUJI1'
                defaultMessage='-'
                description='Hyphen'
              />
            )}
          </Typography>
          <Box tag='div' className='ServiceAccountListItem-content'>
            <Box
              tag='div'
              className='ServiceAccountListItem-contentApplication'>
              <ApplicationIcon
                size='xxSmall'
                src={getLogoEndpointUrl(endpointLogo ?? undefined)}
              />
              <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1}>
                {endpointname}
              </Typography>
            </Box>
            {description && description.trim().length > 0 ? (
              <>
                <InterPoint />
                <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
                  {description}
                </Typography>
              </>
            ) : null}
          </Box>
        </Box>
      }
      isChecked={isChecked}
      handleCheckBoxChange={() => handleClick(id)}
    />
  );
}

const DEFAULT_LIST_STATE = {
  currentOffset: 0,
  totalCount: null as number | null,
  isObserverInView: true,
  isListError: false,
  isListLoading: false,
};

interface ServiceAccountModalProps {
  isFullView: boolean;
  retentions?: ServiceAccountListResponse[];
  fetchList: (pagination?: EndpointParams) => Promise<unknown>;
  setChecked: React.Dispatch<React.SetStateAction<CustomListModalTypes[]>>;
  currentMode: string;
  setInlineItemCount: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * ServiceAccount
 */
function ServiceAccount({
  isFullView,
  retentions,
  fetchList,
  setChecked,
  currentMode,
  setInlineItemCount,
}: ServiceAccountModalProps) {
  const [listControls, setListControls] = useState({
    searchText: '',
  });
  const [listState, setListState] = useState(DEFAULT_LIST_STATE);
  const [listRows, setListRows] = useState<TrackedListItem[]>(
    [] as TrackedListItem[]
  );
  const [activeRows, setActiveRows] = useState<TrackedListItem[]>(
    retentions?.map((item) => ({
      ...item,
      tracker: {
        id: item.accountKey,
        isChecked: item.isChecked,
      },
    })) ?? []
  );

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        !listState.isListLoading &&
          (listState.totalCount === null ||
            (listState.currentOffset < listState.totalCount &&
              listRows.length < listState.totalCount))
      ),
    [
      listState.currentOffset,
      listState.totalCount,
      listRows.length,
      listState.isListLoading,
    ]
  );
  const isZeroCount = useMemo(
    () =>
      Boolean(
        listRows.length === 0 &&
          listState.totalCount !== null &&
          !isValidApiCall
      ),
    [isValidApiCall, listRows.length, listState.totalCount]
  );
  const isFilterApplied = useMemo(
    () => listControls.searchText.trim().length > 0,
    [listControls.searchText]
  );

  // List management
  const listObserverElement = useInViewObserver(({ isInView }) => {
    setListState((prev) => ({ ...prev, isObserverInView: isInView }));
  });

  useEffect(() => {
    if (!listState.isObserverInView || !isValidApiCall) {
      return;
    }

    Logger.log(
      'Fetching',
      listState.currentOffset,
      'to',
      listState.currentOffset + MAX_COUNT
    );

    setListState((prev) => ({
      ...prev,
      isListLoading: true,
      isListError: false,
    }));

    const payload = isFullView
      ? {
          max: MAX_COUNT,
          offset: listState.currentOffset,
          payload: { wsearch: listControls.searchText },
        }
      : undefined;

    fetchList(payload)
      .then((response) => {
        const { totalRecords, data } = response as {
          totalRecords: number;
          data: ServiceAccountListResponse[] | null | undefined;
        };

        if (!data) {
          setListState((prev) => ({ ...prev, totalCount: 0 }));

          return;
        }

        if (
          totalRecords > LIMIT_INLINE_COUNT &&
          currentMode === WRAPPER_MODE.INLINE
        ) {
          setInlineItemCount(data.length);
        }

        const trackedResponse = data.map((item) => ({
          ...item,
          tracker: {
            id: item.accountKey,
            isChecked:
              item.isChecked ||
              Boolean(activeRows.find((o) => o.tracker.id === item.accountKey)),
          },
        })) as TrackedListItem[];

        setListRows((prev) => [...prev, ...trackedResponse]);
        setListState((prev) => ({
          ...prev,
          totalCount: totalRecords,
          currentOffset: prev.currentOffset + MAX_COUNT,
        }));
      })
      .catch((error) => {
        Logger.log('Received error while fetching: ', error);

        setListState((prev) => ({
          ...prev,
          isListError: true,
        }));
      })
      .finally(() => {
        setListState((prev) => ({
          ...prev,
          isListLoading: false,
          isObserverInView: false,
        }));
      });
  }, [
    isValidApiCall,
    fetchList,
    activeRows,
    isFullView,
    listState.isObserverInView,
    listState.currentOffset,
    listControls.searchText,
    currentMode,
    setInlineItemCount,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffectAfterMount(() => {
    const debounceTimer = setTimeout(() => {
      setListState(DEFAULT_LIST_STATE);
      setListRows([]);
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [listControls.searchText]);

  // Setting selections
  useEffect(() => {
    setActiveRows(listRows.filter((e) => e.tracker.isChecked));
  }, [listRows]);

  useEffect(() => {
    setChecked(
      activeRows.map(({ tracker, ...rest }) => ({ ...rest, isChecked: true }))
    );
  }, [activeRows, setChecked]);

  // Utils
  const handleClick = useCallback((id: number) => {
    setListRows((prev) =>
      prev.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              isChecked: !item.tracker.isChecked,
            },
          };
        }

        return {
          ...item,
          tracker: {
            ...item.tracker,
            isChecked: false,
          },
        };
      })
    );
  }, []);

  const handleSearch = useCallback((text: string) => {
    setListControls((prev) => ({ ...prev, searchText: text }));
  }, []);

  return (
    <Box tag='div' className='ServiceAccount'>
      {isFullView && (
        <>
          <Box tag='div'>
            <Typography kind='h2' color={CF_SEC_BLD}>
              <FormattedMessage
                id='0hWKpM'
                defaultMessage='Select Service Accounts'
                description='Heading for service account selection list viewer'
              />
            </Typography>
            <Typography kind='body2' color={CF_NEU_MED}>
              <FormattedMessage
                id='WjBmbi'
                defaultMessage='Your available options have been curated based on your previous inputs. To see a wider or narrower list of options, update it with a prompt.'
                description='Your available options have been curated based on your previous inputs. To see a wider or narrower list of options, update it with a prompt.'
              />
            </Typography>
          </Box>
          <ListControls
            setSearchText={handleSearch}
            isDisabled={listState.isListLoading}
          />
        </>
      )}
      <Box tag='div' className='ServiceAccount-bodyWrapper'>
        {listRows.map((item) => (
          <ServiceAccountListItem
            key={item.tracker.id}
            isLoading={listState.isListLoading}
            listItem={item}
            handleClick={handleClick}
          />
        ))}
        {listState.isListError && (
          <Box className='ListViewer-errorModal'>
            <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
            <Typography kind='body2' color={CF_CRT_MED}>
              <FormattedMessage
                id='apz7lc'
                defaultMessage='System encountered an error. Please contact the Support Administrator'
                description='System encountered an error. Please contact the Support Administrator'
              />
            </Typography>
          </Box>
        )}
        {/* Observer Element */}
        {!listState.isListError && !listState.isListLoading && isFullView
          ? listObserverElement
          : null}
        {/* Loading Skeleton */}
        {!listState.isListError && listState.isListLoading && (
          <Box className='ListViewer-loadingModal'>
            <ListSkeleton count={3} />
          </Box>
        )}
        {!listState.isListError && isZeroCount && (
          <Box tag='div' className='AccessModal-illustrationWrapper'>
            <Illustration kind='Empty' />
            <Typography kind='h1'>
              {isFilterApplied ? (
                <FormattedMessage
                  id='C0QjhC'
                  defaultMessage='No Results Found!'
                  description='No Results Found!'
                />
              ) : (
                <FormattedMessage
                  id='3uRK0t'
                  defaultMessage='There are no eligible Accounts to select!'
                  description='There are no eligible Accounts to select!'
                />
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

ServiceAccount.defaultProps = {
  retentions: null,
};

export default memo(ServiceAccount);
